import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Accordion, AccordionTab } from 'primereact/accordion'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { StepperRefAttributes } from 'primereact/stepper'

import {
  AttachmentDownloadButton,
  AttachmentOpenInTabButton,
} from '../../../../../../components/attachments'
import { getInvoiceTypeCodeString } from '../../../../../../components/tableTemplates'
import { useToastContext } from '../../../../../../contexts'
import { EInvoiceAttachment, EInvoiceDto } from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { getFormattedFileSize } from '../../../../../../utils/formatUtils'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import { DocumentList, VirusCheckStatus } from '../../../../common'
import { requestCompleteEInvoice } from '../../../../data'
import { LinesData } from '../additional-data/lines'
import { Footer } from '../footer'
import { SummaryField } from './common/SummaryField'

type Props = {
  eInvoice: EInvoiceDto | undefined
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
}
export const Summary: React.FC<Props> = ({ eInvoice, stepperRef }) => {
  const { t, i18n } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const { showToast } = useToastContext()

  const navigate = useNavigate()

  const attachmentsForList =
    eInvoice?.Attachments?.filter(
      att =>
        att.DocumentType === 'FacturX' ||
        att.DocumentType === 'XInvoice' ||
        att.DocumentType === 'ReadableInvoice' ||
        att.DocumentType === 'UpdatedReadableInvoice' ||
        att.DocumentType === 'CheckReport',
    ).sort((a, b) => {
      const order = {
        ReadableInvoice: 1,
        UpdatedReadableInvoice: 2,
        XInvoice: 3,
        FacturX: 4,
        CheckReport: 5,
      }
      return (
        (order[a.DocumentType as keyof typeof order] || 999) -
        (order[b.DocumentType as keyof typeof order] || 999)
      )
    }) ?? []

  const attachmentsForTable =
    eInvoice?.Attachments?.filter(
      att =>
        att.DocumentType === 'EmbeddedDocument' ||
        att.DocumentType === 'AdditionalDocument',
    ) ?? []

  const handleSend = async () => {
    setIsRequestRunning(true)
    try {
      await requestCompleteEInvoice(eInvoice?.id!)

      showToast({
        severity: 'success',
        summary: t(
          'eInvoiceDetails.stepper.footer.send.messages.success.summary',
        ),
        detail: t(
          'eInvoiceDetails.stepper.footer.send.messages.success.detail',
          { invoiceNumber: eInvoice?.Payload?.ID.Value?.Value },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        severity: 'error',
        sticky: true,
        summary: t(
          'eInvoiceDetails.stepper.footer.send.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      navigate('/e-invoices')
      setIsRequestRunning(false)
    }
  }

  const actionBodyTemplate = (rowData: EInvoiceAttachment) => {
    return (
      <>
        <AttachmentOpenInTabButton
          key={`att-tab-${rowData.Id}`}
          fullUrl={rowData.DocServUri as string}
          mimeType={rowData.MimeType as string}
          fileName={rowData.FileName}
        />

        <AttachmentDownloadButton
          key={`att-download-${rowData.Id}`}
          fullUrl={rowData.DocServUri as string}
          mimeType={rowData.MimeType as string}
          fileName={rowData.FileName}
        />
      </>
    )
  }

  const fileSizeTemplate = (rowData: EInvoiceAttachment) => {
    return <>{getFormattedFileSize(rowData.FileSizeInBytes, i18n.language)}</>
  }

  const documentTypeTemplate = (rowData: EInvoiceAttachment) => {
    return (
      <>
        {t(
          `eInvoiceDetails.stepper.additionalDocuments.table.columns.documentType.options.${rowData.DocumentType}`,
        )}
      </>
    )
  }

  const isVirusCheckedTemplate = (rowData: EInvoiceAttachment) => {
    return <VirusCheckStatus virusChecked={rowData.VirusChecked} />
  }

  // Found company code master data
  const foundCompanyCodeMasterDataLabel = (
    <label htmlFor="foundCompanyCodeMasterData" className="text-sm">
      {t(
        'eInvoiceDetails.stepper.summary.invoiceRecipient.form.foundCompanyCodeMasterData.label',
      )}
    </label>
  )

  const foundCompanyCodeId =
    eInvoice?.Payload?.CompanyCodeMasterData?.Common?.DisplayId
  const foundCompanyCodeCompany =
    eInvoice?.Payload?.CompanyCodeMasterData?.Common?.Company
  const foundCompanyCodeMasterDataValue = foundCompanyCodeId
    ? `${foundCompanyCodeId} - ${foundCompanyCodeCompany}`
    : undefined

  const foundCompanyCodeMasterDataComponent = (
    <InputText
      id="foundCompanyCodeMasterData"
      value={foundCompanyCodeMasterDataValue}
      className="w-full"
      disabled
    />
  )

  const foundCompanyCodeMasterDataWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={foundCompanyCodeMasterDataComponent}
      label={foundCompanyCodeMasterDataLabel}
      className="col-12 lg:col-8"
    />
  )

  // Found supplier master data
  const foundSupplierMasterDataLabel = (
    <label htmlFor="foundSupplierMasterData" className="text-sm">
      {t(
        'eInvoiceDetails.stepper.summary.biller.form.foundSupplierMasterData.label',
      )}
    </label>
  )

  const foundSupplierId =
    eInvoice?.Payload?.SupplierMasterData?.Common?.DisplayId
  const foundSupplierCompany =
    eInvoice?.Payload?.SupplierMasterData?.Common?.Company
  const foundSupplierMasterDataValue = foundSupplierId
    ? `${foundSupplierId} - ${foundSupplierCompany}`
    : undefined

  const foundSupplierMasterDataComponent = (
    <InputText
      id="foundSupplierMasterData"
      value={foundSupplierMasterDataValue}
      className="w-full"
      disabled
    />
  )

  const foundSupplierMasterDataWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={foundSupplierMasterDataComponent}
      label={foundSupplierMasterDataLabel}
      className="col-12 lg:col-8"
    />
  )

  return (
    <>
      <Accordion multiple activeIndex={[0, 1, 2, 3, 4, 5, 6, 7]}>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.invoiceOverview.title')}
        >
          <div className="grid">
            {/* BT-1 */}
            <SummaryField
              id="invoiceNumber"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceOverview.form.invoiceNumber.label',
              )}
              value={eInvoice?.Payload?.ID?.Value?.Value ?? undefined}
              trackedValue={eInvoice?.Payload?.ID?.Value}
              config={config.eInvoice.summary.invoiceOverview.invoiceNumber}
            />

            {/* BT-3 */}
            <SummaryField
              id="invoiceType"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceOverview.form.invoiceType.label',
              )}
              value={
                eInvoice?.Payload?.InvoiceTypeCode?.Value
                  ? t(
                      `eInvoiceOverview.table.columns.invoiceType.options.${getInvoiceTypeCodeString(
                        eInvoice.Payload.InvoiceTypeCode.Value,
                      )}`,
                    )
                  : undefined
              }
              trackedValue={eInvoice?.Payload?.InvoiceTypeCode}
              config={config.eInvoice.summary.invoiceOverview.invoiceType}
            />

            {/* BT-2 */}
            <SummaryField
              id="invoiceDate"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceOverview.form.invoiceDate.label',
              )}
              value={
                eInvoice?.Payload?.IssueDate?.Value
                  ? new Date(eInvoice.Payload.IssueDate.Value)
                  : undefined
              }
              trackedValue={eInvoice?.Payload?.IssueDate}
              type="date"
              config={config.eInvoice.summary.invoiceOverview.invoiceDate}
              locale={i18n.language}
            />

            {/* BT-72 */}
            <SummaryField
              id="deliveryDate"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceOverview.form.deliveryDate.label',
              )}
              value={
                eInvoice?.Payload?.Delivery?.ActualDeliveryDate?.Value
                  ? new Date(eInvoice.Payload.Delivery.ActualDeliveryDate.Value)
                  : undefined
              }
              trackedValue={eInvoice?.Payload?.Delivery?.ActualDeliveryDate}
              type="date"
              config={config.eInvoice.summary.invoiceOverview.deliveryDate}
              locale={i18n.language}
            />

            {/* BT-115 */}
            <SummaryField
              id="totalAmountGross"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceOverview.form.totalAmountGross.label',
              )}
              value={
                eInvoice?.Payload?.LegalMonetaryTotal?.PayableAmount?.Value
                  ?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.LegalMonetaryTotal?.PayableAmount?.Value
                  ? {
                      ...eInvoice.Payload.LegalMonetaryTotal.PayableAmount
                        .Value,
                      Value:
                        eInvoice.Payload.LegalMonetaryTotal.PayableAmount.Value.Value?.toString() ??
                        null,
                      OriginalValue:
                        eInvoice.Payload.LegalMonetaryTotal.PayableAmount.Value.OriginalValue?.toString() ??
                        null,
                    }
                  : undefined
              }
              type="number"
              config={config.eInvoice.summary.invoiceOverview.totalAmountGross}
              locale={i18n.language}
              currency={
                eInvoice?.Payload?.LegalMonetaryTotal?.PayableAmount?.CurrencyID
                  ?.Value ?? undefined
              }
            />
            <SummaryField
              id="invoiceClassification"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceOverview.form.invoiceClassification.label',
              )}
              value={
                eInvoice?.Meta?.controlInformation?.invoiceClassification
                  ? t(
                      `eInvoiceOverview.table.columns.invoiceClassification.options.${eInvoice.Meta.controlInformation.invoiceClassification}`,
                    )
                  : undefined
              }
              config={
                config.eInvoice.summary.invoiceOverview.invoiceClassification
              }
            />
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.invoiceRecipient.title')}
        >
          <div className="grid">
            {/* BT-46 */}
            <SummaryField
              id="customerId"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerId.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PartyIdentification?.at(
                  0,
                )?.ID?.Value?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PartyIdentification?.at(
                  0,
                )?.ID?.Value
              }
              config={config.eInvoice.summary.invoiceRecipient.customerId}
            />
            {/* BT-44 */}
            <SummaryField
              id="customerName"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerName.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party
                  ?.PartyLegalEntity?.RegistrationName?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party
                  ?.PartyLegalEntity?.RegistrationName
              }
              config={config.eInvoice.summary.invoiceRecipient.customerName}
            />
            {/* BT-45 */}
            <SummaryField
              id="customerCompanyId"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerPartyName.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty.Party?.PartyName
                  ?.Name?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty.Party?.PartyName
                  ?.Name
              }
              config={
                config.eInvoice.summary.invoiceRecipient.customerPartyName
              }
            />
            {/* BT-48 */}
            <SummaryField
              id="customerCompanyId"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerCompanyId.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PartyTaxScheme?.find(
                  x => x?.TaxScheme?.ID?.Value?.Value === 'VAT',
                )?.CompanyID?.Value?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PartyTaxScheme?.find(
                  x => x?.TaxScheme?.ID?.Value?.Value === 'VAT',
                )?.CompanyID?.Value
              }
              config={
                config.eInvoice.summary.invoiceRecipient.customerCompanyId
              }
            />
            {/* BT-50 */}
            <SummaryField
              id="customerStreet"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerStreet.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.StreetName?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.StreetName
              }
              config={config.eInvoice.summary.invoiceRecipient.customerStreet}
            />
            {/* BT-53 */}
            <SummaryField
              id="customerPostalCode"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerPostalCode.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.PostalZone?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.PostalZone
              }
              config={
                config.eInvoice.summary.invoiceRecipient.customerPostalCode
              }
            />
            {/* BT-52 */}
            <SummaryField
              id="customerCity"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerCity.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.CityName?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.CityName
              }
              config={config.eInvoice.summary.invoiceRecipient.customerCity}
            />
            {/* BT-55 */}
            <SummaryField
              id="customerCountry"
              label={t(
                'eInvoiceDetails.stepper.summary.invoiceRecipient.form.customerCountry.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.Country?.IdentificationCode?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PostalAddress
                  ?.Country?.IdentificationCode
              }
              config={config.eInvoice.summary.invoiceRecipient.customerCountry}
            />

            {foundCompanyCodeMasterDataWrapper}
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.biller.title')}
        >
          <div className="grid">
            {/* BT-29 */}
            <SummaryField
              id="sellerId"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerId.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PartyIdentification?.at(
                  0,
                )?.ID?.Value?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PartyIdentification?.at(
                  0,
                )?.ID?.Value
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerId}
            />

            {/* BT-27 */}
            <SummaryField
              id="sellerName"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerRegistrationName.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty.Party
                  .PartyLegalEntity.RegistrationName?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty.Party
                  .PartyLegalEntity.RegistrationName
              }
              config={
                config.eInvoice.summary.invoiceIssuer.sellerRegistrationName
              }
            />

            {/* BT-28 */}
            <SummaryField
              id="sellerName"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerName.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PartyName
                  ?.Name?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PartyName
                  ?.Name
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerName}
            />

            {/* BT-31 */}
            <SummaryField
              id="sellerCompanyId"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerCompanyId.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PartyTaxScheme?.find(
                  x => x?.TaxScheme?.ID?.Value?.Value === 'VAT',
                )?.CompanyID?.Value?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.PartyTaxScheme?.find(
                  x => x?.TaxScheme?.ID?.Value?.Value === 'VAT',
                )?.CompanyID?.Value
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerCompanyId}
            />

            {/* BT-35 */}
            <SummaryField
              id="sellerStreet"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerStreet.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.StreetName?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.StreetName
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerStreet}
            />

            {/* BT-38 */}
            <SummaryField
              id="sellerPostalCode"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerPostalCode.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.PostalZone?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.PostalZone
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerPostalCode}
            />

            {/* BT-37 */}
            <SummaryField
              id="sellerCity"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerCity.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.CityName?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.CityName
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerCity}
            />

            {/* BT-40 */}
            <SummaryField
              id="sellerCountry"
              label={t(
                'eInvoiceDetails.stepper.summary.biller.form.sellerCountry.label',
              )}
              value={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.Country?.IdentificationCode?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingSupplierParty?.Party?.PostalAddress
                  ?.Country?.IdentificationCode
              }
              config={config.eInvoice.summary.invoiceIssuer.sellerCountry}
            />

            {foundSupplierMasterDataWrapper}
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.accountingCustomer.title')}
        >
          <div className="grid">
            {/* BT-58 */}
            <SummaryField
              id="buyerEmail"
              label={t(
                'eInvoiceDetails.stepper.summary.accountingCustomer.form.buyerEmail.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.Contact
                  ?.ElectronicMail?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.Contact
                  ?.ElectronicMail
              }
              config={config.eInvoice.summary.client.buyerEmail}
            />
          </div>

          <div className="grid">
            {/* BT-56 */}
            <SummaryField
              id="buyerName"
              label={t(
                'eInvoiceDetails.stepper.summary.accountingCustomer.form.buyerName.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.Contact?.Name
                  ?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.Contact?.Name
              }
              config={config.eInvoice.summary.client.buyerName}
            />
            {/* BT-10 */}
            <SummaryField
              id="buyerRoutingId"
              label={t(
                'eInvoiceDetails.stepper.summary.accountingCustomer.form.buyerRoutingId.label',
              )}
              value={eInvoice?.Payload?.BuyerReference?.Value ?? undefined}
              trackedValue={eInvoice?.Payload?.BuyerReference}
              config={config.eInvoice.summary.client.buyerRoutingId}
            />

            {/* BT-57 */}
            <SummaryField
              id="buyerPhone"
              label={t(
                'eInvoiceDetails.stepper.summary.accountingCustomer.form.buyerPhone.label',
              )}
              value={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.Contact
                  ?.Telephone?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AccountingCustomerParty?.Party?.Contact
                  ?.Telephone
              }
              config={config.eInvoice.summary.client.buyerPhone}
            />
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.references.title')}
        >
          <div className="grid">
            {/* BT-11 */}
            <SummaryField
              id="projectId"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.projectId.label',
              )}
              value={
                eInvoice?.Payload?.ProjectReference?.ID?.Value?.Value ??
                undefined
              }
              trackedValue={eInvoice?.Payload?.ProjectReference?.ID?.Value}
              config={config.eInvoice.summary.invoiceReferences.projectId}
            />

            {/* BT-12 */}
            <SummaryField
              id="contractNumber"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.contractNumber.label',
              )}
              value={
                eInvoice?.Payload?.ContractDocumentReference?.ID?.Value
                  ?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.ContractDocumentReference?.ID?.Value
              }
              config={config.eInvoice.summary.invoiceReferences.contractNumber}
            />

            {/* BT-13 */}
            <SummaryField
              id="orderNumber"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.orderNumber.label',
              )}
              value={
                eInvoice?.Payload?.OrderReference?.ID?.Value?.Value ?? undefined
              }
              trackedValue={eInvoice?.Payload?.OrderReference?.ID?.Value}
              config={config.eInvoice.summary.invoiceReferences.orderNumber}
            />

            {/* BT-14 */}
            <SummaryField
              id="salesOrderNumber"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.salesOrderNumber.label',
              )}
              value={
                eInvoice?.Payload?.OrderReference?.SalesOrderID?.Value ??
                undefined
              }
              trackedValue={eInvoice?.Payload?.OrderReference?.SalesOrderID}
              config={
                config.eInvoice.summary.invoiceReferences.salesOrderNumber
              }
            />

            {/* BT-15 */}
            <SummaryField
              id="receiptDocument"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.receiptDocument.label',
              )}
              value={
                eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value?.Value ??
                undefined
              }
              trackedValue={
                eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value
              }
              config={config.eInvoice.summary.invoiceReferences.receiptDocument}
            />

            {/* BT-16 */}
            <SummaryField
              id="despatchDocument"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.despatchDocument.label',
              )}
              value={
                eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value
                  ?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value
              }
              config={
                config.eInvoice.summary.invoiceReferences.despatchDocument
              }
            />

            {/* BT-17 */}
            <SummaryField
              id="originatorDocument"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.originatorDocument.label',
              )}
              value={
                eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value
                  ?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value
              }
              config={
                config.eInvoice.summary.invoiceReferences.originatorDocument
              }
            />

            {/* BT-18 */}
            <SummaryField
              id="objectReference"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.objectReference.label',
              )}
              value={
                eInvoice?.Payload?.AdditionalDocumentReference?.find(
                  x => x?.DocumentTypeCode?.Value === '130',
                )?.ID?.Value?.Value ?? undefined
              }
              trackedValue={
                eInvoice?.Payload?.AdditionalDocumentReference?.find(
                  x => x?.DocumentTypeCode?.Value === '130',
                )?.ID?.Value
              }
              config={config.eInvoice.summary.invoiceReferences.objectReference}
            />

            {/* BT-19 */}
            <SummaryField
              id="accountingCost"
              label={t(
                'eInvoiceDetails.stepper.summary.references.form.accountingCost.label',
              )}
              value={eInvoice?.Payload?.AccountingCost?.Value ?? undefined}
              trackedValue={eInvoice?.Payload?.AccountingCost}
              config={config.eInvoice.summary.invoiceReferences.accountingCost}
            />
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.additionalData.positions.title')}
        >
          <div className="grid">
            <div className="col-12">
              <LinesData />
            </div>
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.remarks.title')}
        >
          <div className="grid">
            {/* BT-22 */}
            <SummaryField
              id="additionalRemarks"
              label={t(
                'eInvoiceDetails.stepper.summary.remarks.form.additionalRemarks.label',
              )}
              value={eInvoice?.Payload?.Note?.Value ?? undefined}
              trackedValue={eInvoice?.Payload?.Note}
              config={config.eInvoice.summary.remarks.additionalRemarks}
              className="col-12"
              type="textarea"
              rows={3}
            />
          </div>
        </AccordionTab>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.summary.documents.title')}
        >
          <DocumentList documents={attachmentsForList} />
          <div className="mt-4">
            <h3 className="mb-3">
              {t(
                'eInvoiceDetails.stepper.summary.documents.additionalDocuments.title',
              )}
            </h3>
            <DataTable
              value={attachmentsForTable}
              emptyMessage={t(
                'eInvoiceDetails.stepper.additionalDocuments.table.emptyMessage',
              )}
            >
              {/* BT-122 */}
              <Column
                field="FileName"
                header={t(
                  'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.fileName.header',
                )}
              />
              {/* BT-123 */}
              <Column
                field="Description"
                header={t(
                  'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.description.header',
                )}
              />
              <Column
                header={t(
                  'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.documentType.header',
                )}
                body={documentTypeTemplate}
              />
              <Column
                header={t(
                  'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.fileSize.header',
                )}
                body={fileSizeTemplate}
              />
              <Column
                header={t(
                  'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.virusChecked.header',
                )}
                body={isVirusCheckedTemplate}
                align="center"
              />
              <Column
                header={t(
                  'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.attachment.header',
                )}
                key="attachmentButtons"
                body={actionBodyTemplate}
                align="right"
              />
            </DataTable>
          </div>
        </AccordionTab>
      </Accordion>

      <Footer
        stepperRef={stepperRef}
        isLastStep
        sendButtonCallback={handleSend}
        isRequestRunning={isRequestRunning}
      />
    </>
  )
}
